import React from 'react';
import { useForm } from 'react-hook-form';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useWizard } from '../../contexts/WizardContext';

const ContactInfo = () => {
  const { formData, updateFormData, nextStep } = useWizard();
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      first_name: formData.first_name,
      last_name: formData.last_name,
      applicant_email: formData.applicant_email,
      applicant_phone: formData.applicant_phone,
      date_of_birth: formData.date_of_birth,
    }
  });

  const dateOfBirth = watch('date_of_birth');

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(dateOfBirth);

  const onSubmit = (data) => {
    updateFormData({
      ...data,
      age: age
    });
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="first_name">First Name *</Label>
          <Input
            id="first_name"
            data-testid="first-name-input"
            {...register('first_name', { required: 'First name is required' })}
            className="h-12 mt-1.5"
          />
          {errors.first_name && (
            <p className="text-sm text-red-600 mt-1">{errors.first_name.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="last_name">Last Name *</Label>
          <Input
            id="last_name"
            data-testid="last-name-input"
            {...register('last_name', { required: 'Last name is required' })}
            className="h-12 mt-1.5"
          />
          {errors.last_name && (
            <p className="text-sm text-red-600 mt-1">{errors.last_name.message}</p>
          )}
        </div>
      </div>

      <div>
        <Label htmlFor="applicant_email">Email Address *</Label>
        <Input
          id="applicant_email"
          data-testid="applicant-email-input"
          type="email"
          {...register('applicant_email', { 
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          })}
          className="h-12 mt-1.5"
        />
        {errors.applicant_email && (
          <p className="text-sm text-red-600 mt-1">{errors.applicant_email.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="applicant_phone">Phone Number *</Label>
        <Input
          id="applicant_phone"
          data-testid="applicant-phone-input"
          type="tel"
          {...register('applicant_phone', { required: 'Phone is required' })}
          className="h-12 mt-1.5"
          placeholder="(555) 123-4567"
        />
        {errors.applicant_phone && (
          <p className="text-sm text-red-600 mt-1">{errors.applicant_phone.message}</p>
        )}
      </div>

      <div>
        <Label htmlFor="date_of_birth">Date of Birth (Optional)</Label>
        <Input
          id="date_of_birth"
          data-testid="date-of-birth-input"
          type="date"
          {...register('date_of_birth')}
          className="h-12 mt-1.5"
        />
        {age !== null && (
          <p className="text-sm text-slate-600 mt-2">Age: {age} years</p>
        )}
      </div>

      <ThemedButton 
        type="submit"
        data-testid="contact-info-next-btn"
        variant="wizard" size="full"
      >
        Next: Organization Information
      </ThemedButton>
    </form>
  );
};

export default ContactInfo;
